import { CortexTouch } from './CortexTouch';
import { CortexTouchSource } from "./CortexTouchSource";
import { CortexAppConfig } from "./CortexAppConfig";

const w: Window = window, ls: Storage = w.localStorage;
const defaultConfiguration: CortexAppConfig = new CortexAppConfig();

let configuration: CortexAppConfig = { ...defaultConfiguration };

/**
 * Initialise the tracking library
 *
 * @export
 * @description This is automatically called and only needs to be called again if configuration changes are rquired.
 * @param {CortexAppConfig} configOverrides
 */
export function init(configOverrides?: CortexAppConfig) {
  configuration = { ...defaultConfiguration, ...configOverrides };

  if (configuration.debugMode) {
    console.debug("CortexApp: Debug mode enabled");
  }
  configuration.autoDetectTouches
    ? w.addEventListener("load", detectTouches)
    : w.removeEventListener("load", detectTouches);

  if (configuration.debugMode) {
    console.debug(configuration.autoDetectTouches
      ? "CortexApp: Auto touch detection" : "CortexApp: Manual touch detection");
  }

  for (let i = 0; i < document.forms.length; i++) {
    const form: HTMLFormElement = document.forms[i];
    configuration.autoBindToFormSubmits
      ? form.addEventListener("submit", populateFormBySubmitEvent)
      : form.removeEventListener("submit", populateFormBySubmitEvent);
    if (configuration.debugMode) { console.debug(configuration.autoBindToFormSubmits ? "CortexApp : Listening to form [id=" + form.id + "]" : "CortexApp : Not listening to form [id=" + form.id + "]"); }
  }
}

/**
 * Attach recorded touches to form data
 *
 * @export
 * @description If the form contains an input as specified in configuration.fieldNameToPopulate then set the value of the field to be JSON string of the touch history.
 * @param {Event} submitEvent - form submit event OR submitted
 */
export function populateFormBySubmitEvent(submitEvent: Event) {
  let form: HTMLFormElement = null;
  if (submitEvent instanceof Event) {
    form = submitEvent.target as HTMLFormElement;
  }
  else { return; }

  populateFormByFormObject(form);
}

/**
 * Attach recorded touches to form data
 *
 * @export
 * @description If the form contains an input as specified in configuration.fieldNameToPopulate then set the value of the field to be JSON string of the touch history. Calling this will also call startNewSession()
 * @param {HTMLFormElement} form - form submit event OR
 */
export function populateFormByFormObject(form: HTMLFormElement) {
  if (form instanceof HTMLFormElement) {

    const element: HTMLInputElement = form.elements[configuration.fieldNameToPopulate];

    if (element) {
      if (configuration.debugMode) { console.debug("CortexApp : Populating field " + configuration.fieldNameToPopulate + " on form [id=" + form.id + "]"); }
      const touchData: string = JSON.stringify(getAllTouches(), null, 1);
      const touchDataObjectsOnly: string = touchData.substring(1).slice(0, -1) + ",";
      element.value = configuration.formDataAsArray ? touchData : touchDataObjectsOnly;
      if (configuration.clearSessionOnFormSubmit) {
        startNewSession();
      }
    }
  } else { return; }
}

/**
 * Manually bind to the given form's submit event element to populateFormBySubmitEvent function
 *
 * @export
 * @param {HTMLFormElement} form - html form element
 */
export function listenToFormSubmit(form: HTMLFormElement) {
  form.removeEventListener("submit", populateFormBySubmitEvent);
  form.addEventListener("submit", populateFormBySubmitEvent);
}

/**
 * Manually unbind to the given form's submit event element to populateFormBySubmitEvent function
 *
 * @export
 * @param {HTMLFormElement} form - html form element
 */
export function stopListeningToFormSubmit(form: HTMLFormElement) {
  form.removeEventListener("submit", populateFormBySubmitEvent);
}

/**
 * Get the current Cortex Tracking Configuration
 *
 * @export
 * @return {CortexAppConfig}
 */
export function currentConfiguration(): CortexAppConfig {
  return configuration;
}

/**
 * Parses the current pages meta data for Cortex Touches
 *
 * @export
 */
export function detectTouches() {
  const queryStringVariables = new Proxy(
    new URLSearchParams(window.location.search),
    {
      get: (searchqueryStringVariables, prop: string) =>
        searchqueryStringVariables.get(prop),
    }
  );

  const t: CortexTouch = new CortexTouch();
  let hasValue = false;

  configuration.queryStringToCheck.forEach((qs: string) => {
    const v = queryStringVariables[qs];
    if (v != null) { t.qsValues[qs] = v; }
    hasValue = hasValue || (v != null);
  });

  addTouchToHistory(t);
}

/**
 * Get the first recorded touch object
 *
 * @export
 * @return {(CortexTouch|null)}
 */
export function getFirstTouch(): CortexTouch {
  const at: CortexTouch[] = getAllTouches();
  return at.length == 0 ? null : at[0];
}

/**
 * Get the last recorded touch object
 *
 * @export
 * @return {(CortexTouch|null)}
 */
export function getLastTouch(): CortexTouch {
  return JSON.parse(ls.getItem("lastTouch"));
}

/**
 * Get all recorded touches
 *
 * @export
 * @return {(CortexTouch[]|[])}
 */
export function getAllTouches(): CortexTouch[] {
  const at = JSON.parse(ls.getItem("allTouches")) || [];
  if (at.length > 0) {
    return at.sort((a: { timestamp: number; }, b: { timestamp: number; }) => b.timestamp - a.timestamp);
  } else {
    return at;
  }
}

/**
 * Clear all recorded touches
 *
 * @export
 */
export function startNewSession() {
  if (configuration.debugMode) { console.debug("CortexApp : Clearing touch history"); }
  ls.removeItem("allTouches");
}

/**
 * Add a touch object into the current history of touches.
 *
 * @export
 * @param {CortexTouch} oTouch
 */
export function addTouchToHistory(oTouch: CortexTouch) {
  const se = new Date(JSON.parse(ls.getItem("sessionExpires")));
  const lt = getLastTouch();

  const neverLoggedTouch: boolean = (se == null || lt == null);
  const sessionExpiryPassed: boolean = se < oTouch.timestamp;
  const paramsHaveChanged: boolean = lt != null && JSON.stringify(lt.qsValues) != JSON.stringify(oTouch.qsValues);
  const externalWithoutParams: boolean = oTouch.touchSource == CortexTouchSource.External && Object.keys(oTouch.qsValues).length == 0;
  const sourceHasChanged: boolean = lt != null && lt.touchSource != oTouch.touchSource;

  // log only when needed
  if (
    neverLoggedTouch ||
    sessionExpiryPassed ||
    paramsHaveChanged ||
    externalWithoutParams ||
    sourceHasChanged
  ) {
    // add to touches
    const at: CortexTouch[] = getAllTouches() || [];
    at.push(oTouch);
    ls.setItem("allTouches", JSON.stringify(at));
    ls.setItem("lastTouch", JSON.stringify(oTouch))
  }
  const expires: Date = new Date(
    new Date(Date.now()).getTime() +
    configuration.sessionExpiryInMinutes * 60000
  );
  ls.setItem("sessionExpires", JSON.stringify(expires));
  if (configuration.debugMode) { console.debug("CortexApp : Touch added to history", oTouch); }
}
// client specific overides

const cso: CortexAppConfig = new CortexAppConfig();

// default init
init(cso);

export default {
  init,
  currentConfiguration,
  detectTouches,
  getFirstTouch,
  getLastTouch,
  getAllTouches,
  addTouchToHistory,
  populateFormByFormObject,
  populateFormBySubmitEvent,
  listenToFormSubmit,
  stopListeningToFormSubmit,
  startNewSession,
  CortexTouch,
  CortexTouchSource,
  CortexAppConfig
};
